import React, { FC } from 'react';
import { DmsTableRowModel } from './dms-list';
import { formatNumberWithCommas, getDisplayDate, getDisplayValue } from '@web/utils';
import { DATE_FORMAT } from '@web/config/constants';
import { StyleProp, TextStyle } from 'react-native';
import { TableCell, TableRow } from '@web/components/table';
import { Icon, Typography } from '@packages/ui/shared';
import { Link } from 'react-router-dom';
import { JobFieldCell } from './dms-job-cell';
import { createUseStyles } from 'react-jss';

interface DmsTableRowProps {
    rowData: DmsTableRowModel;
    expandedRowId: string | null;
    setExpandedRowId: (id: string | null) => void;
}

const useDmsListStyles = createUseStyles({
    jobCell: {
        minWidth: '165px',
    },
    link: {
        fontSize: 15,
        fontWeight: 400,
        '&:hover': {
            textDecoration: 'underline!important',
        },
    },
    jobsCountWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    jobsCountInteractive: {
        cursor: 'pointer',
        '&:hover': {
            opacity: 0.85,
        },
    },
    expandedJobRow: {
        background: '#F8F8F8',
        '& td': {
            background: '#F8F8F8',
        },
    },
});

export const DmsTableRow: FC<DmsTableRowProps> = ({ rowData, expandedRowId, setExpandedRowId }) => {
    const classes = useDmsListStyles();
    const { rowId, roNumber, closedDate, mileage, details } = rowData;
    const { salesType, cause, complaint, correction } = details?.[0] || {};
    const hasMoreJobs = (details?.length ?? 0) > 1;
    const expanded = expandedRowId === rowId;

    const toggleExpanded = () => {
        if (!hasMoreJobs) return;
        if (expanded) {
            setExpandedRowId(null);
        } else {
            setExpandedRowId(rowId);
        }
    };

    const getDisplayValueSafe = (val: any) => {
        if (!val) return getDisplayValue(val);
        switch (val) {
            case 'C':
                return 'Customer Pay';
            case 'W':
                return 'Warranty';
            case 'I':
                return 'Internal';
            default:
                return getDisplayValue(val);
        }
    };

    const typographyStyles = {
        display: 'block',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        cursor: 'pointer',
    } as unknown as StyleProp<TextStyle>;

    return (
        <>
            <TableRow key={rowId}>
                <TableCell>
                    <Typography numberOfLines={1} variant="label">
                        <Link className={classes.link} to={`./${roNumber}`}>
                            {getDisplayValue(roNumber)}
                        </Link>
                    </Typography>
                </TableCell>
                <TableCell>{getDisplayDate(closedDate, DATE_FORMAT.MON_DAY_YEAR)}</TableCell>
                <TableCell>{formatNumberWithCommas(mileage)} mi</TableCell>
                <TableCell>
                    <div
                        className={`${classes.jobsCountWrapper} ${hasMoreJobs ? classes.jobsCountInteractive : ''}`}
                        onClick={toggleExpanded}
                        role={hasMoreJobs ? 'button' : undefined}
                        aria-expanded={hasMoreJobs ? expanded : undefined}
                        aria-label={hasMoreJobs ? (expanded ? 'Collapse jobs' : 'Expand jobs') : undefined}
                    >
                        <Typography numberOfLines={1}>
                            {typeof details?.length === 'number'
                                ? `${details!.length} ${details!.length === 1 ? 'Job' : 'Jobs'}`
                                : 'N/A'}
                        </Typography>
                        {hasMoreJobs && (
                            <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={24} color="grayFive" />
                        )}
                    </div>
                </TableCell>
                <JobFieldCell
                    rowId={rowId}
                    rawValue={complaint}
                    tooltipPrefix="complaint"
                    className={classes.jobCell}
                    typographyStyles={typographyStyles}
                />
                <JobFieldCell
                    rowId={rowId}
                    rawValue={cause}
                    tooltipPrefix="cause"
                    className={classes.jobCell}
                    typographyStyles={typographyStyles}
                />
                <JobFieldCell
                    rowId={rowId}
                    rawValue={correction}
                    tooltipPrefix="correction"
                    className={classes.jobCell}
                    typographyStyles={typographyStyles}
                />
                <JobFieldCell
                    rowId={rowId}
                    rawValue={salesType}
                    tooltipPrefix="salesType"
                    className={classes.jobCell}
                    typographyStyles={typographyStyles}
                    formatValue={(val) => getDisplayValueSafe(val)}
                />
            </TableRow>
            {expanded &&
                hasMoreJobs &&
                details?.slice(1).map((job, idx) => {
                    const addRowId = `${rowId}-job-${idx + 1}`;
                    return (
                        <TableRow key={addRowId} className={classes.expandedJobRow}>
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <TableCell />
                            <JobFieldCell
                                rowId={addRowId}
                                rawValue={job.complaint}
                                tooltipPrefix="complaint"
                                className={classes.jobCell}
                                typographyStyles={typographyStyles}
                            />
                            <JobFieldCell
                                rowId={addRowId}
                                rawValue={job.cause}
                                tooltipPrefix="cause"
                                className={classes.jobCell}
                                typographyStyles={typographyStyles}
                            />
                            <JobFieldCell
                                rowId={addRowId}
                                rawValue={job.correction}
                                tooltipPrefix="correction"
                                className={classes.jobCell}
                                typographyStyles={typographyStyles}
                            />
                            <JobFieldCell
                                rowId={addRowId}
                                rawValue={job.salesType}
                                tooltipPrefix="salesType"
                                className={classes.jobCell}
                                typographyStyles={typographyStyles}
                                formatValue={(val) => getDisplayValueSafe(val)}
                            />
                        </TableRow>
                    );
                })}
        </>
    );
};
